import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { escalateTicketSchema, type EscalateTicketInput } from '@pms/shared';
import { techSupportService } from '@/services/tech-support.service';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface StaffUser { id: string; name: string; }

interface Props {
  ticketId: string;
  currentLevel: number;
  onClose: () => void;
}

export function TicketEscalateModal({ ticketId, currentLevel, onClose }: Props) {
  const qc = useQueryClient();

  const { data: staffData } = useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => api.get<{ data: StaffUser[] }>('/users?limit=200&status=active'),
  });
  const staff: StaffUser[] = staffData?.data?.data ?? [];

  const { register, control, handleSubmit, formState: { errors } } = useForm<EscalateTicketInput>({
    resolver: zodResolver(escalateTicketSchema),
  });

  const mutation = useMutation({
    mutationFn: (data: EscalateTicketInput) => techSupportService.escalateTicket(ticketId, data),
    onSuccess: () => {
      toast.success(`Escalated to level ${currentLevel + 1}`);
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets', ticketId] });
      onClose();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to escalate ticket'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Escalate Ticket</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Current level: {currentLevel} → Level {currentLevel + 1}
          </p>
        </DialogHeader>

        {currentLevel >= 3 ? (
          <p className="py-2 text-sm text-muted-foreground text-center">
            Maximum escalation level reached.
          </p>
        ) : (
          <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Escalate To *</Label>
              <Controller
                name="escalated_to"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.escalated_to && <p className="text-xs text-destructive">{errors.escalated_to.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea id="reason" {...register('reason')} rows={3} placeholder="Why is this being escalated?" />
              {errors.reason && <p className="text-xs text-destructive">{errors.reason.message}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Escalating…' : 'Escalate'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
